import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { DEFAULT_LANGUAGE, LANGUAGES, t as translate, type Language } from "./generated/i18n.js";

/**
 * Le nove lingue, dalle stesse stringhe dell'app desktop.
 *
 * La regola del progetto vale anche qui: **ogni stringa visibile passa di
 * qui**, e le chiavi esistono in tutte le lingue. Niente `|| "fallback"` nei
 * componenti — un fallback inline e' una stringa che non verra' mai tradotta e
 * che nessuno ritrovera' piu'.
 */

const STORAGE_KEY = "lms.lang";

interface I18nValue {
  lang: string;
  setLang: (code: string) => void;
  /** `vars` sostituisce i segnaposto `{nome}` nella stringa tradotta, come
   *  `I18n.t(key, vars)` nell'app desktop. Alcune voci ce li hanno — "Add band
   *  T{p}", "{p} bands" — e senza questo si leggerebbe la graffa. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  languages: readonly Language[];
  /** Le etichette dei PDF, tradotte, da mandare al server nel corpo della
   *  chiamata. Il testo dei report vive nel generatore, che non conosce la
   *  lingua scelta: senza queste il documento esce in inglese qualunque sia
   *  l'interfaccia. E' lo stesso `I18n.reportLabels()` dell'app desktop. */
  reportLabels: () => Record<string, string>;
}

/** Le chiavi che il generatore dei PDF si aspetta. Stessa lista del `.qml`:
 *  mandarne una in meno lascia quella voce in inglese in mezzo a un
 *  documento tradotto. */
const REPORT_KEYS = [
  "rep.period", "rep.generated", "rep.confidential", "rep.pageOf", "rep.subtitle",
  "rep.daily", "rep.weekly", "rep.monthly", "rep.annual",
  "rep.qGood", "rep.qModerate", "rep.qPoor",
  "rep.emissionReport", "rep.siteEmissionReport",
  "rep.totalEmissions", "rep.totalSiteEmissions", "rep.uncertainty",
  "rep.dataQuality", "rep.dataCoverage",
  "rep.colPeriod", "rep.colMass", "rep.colQuality", "rep.colReadings",
  "rep.colCluster", "rep.colCoverage",
  "rep.dailyLeakReport", "rep.site", "rep.area", "rep.reportDate",
  "rep.leakEvents", "rep.none", "rep.l1Threshold", "rep.generatedBy",
  "rep.internalDoc", "rep.noDetections", "rep.noDetectionsBody", "rep.leakOf",
  "rep.colDate", "rep.colTime", "rep.colConcentration",
  "rep.fig", "rep.capPlant", "rep.capPlantNa", "rep.capCamera",
  "rep.capCameraNa", "rep.capAt",
  "rep.noCameraImage", "rep.noCameraBody", "rep.noCameraFile",
  "rep.noPlantView", "rep.noPlantBody",
] as const;

const I18nContext = createContext<I18nValue | undefined>(undefined);

function initialLang(): string {
  // La lingua e' una preferenza della POSTAZIONE, non dell'utente: davanti a
  // un impianto il monitor e' sempre lo stesso e chi ci passa cambia. Tenerla
  // nel profilo utente vorrebbe dire che il turno di notte trova la pagina in
  // un'altra lingua.
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
  } catch {
    /* storage negato: si prosegue col default */
  }
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  return LANGUAGES.some((l) => l.code === nav) ? nav! : DEFAULT_LANGUAGE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState(initialLang);

  const setLang = useCallback((code: string) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage negato: la scelta vale per questa sessione */
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang,
      t: (key: string, vars?: Record<string, string | number>) => {
        const s = translate(lang, key);
        if (!vars) return s;
        return Object.entries(vars).reduce(
          (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
          s,
        );
      },
      languages: LANGUAGES,
      reportLabels: () =>
        Object.fromEntries(REPORT_KEYS.map((k) => [k, translate(lang, k)])),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error("useI18n fuori da <I18nProvider>");
  return v;
}
